import type { BlockRecord } from './types';

export const FAMILY_ROOT_TITLE = 'Family';
export const PERSONAL_ROOT_TITLE = 'Personal';
export const PERSONAL_SECTION_LABEL = 'Personal';

type WorkspaceRootBlock = Pick<BlockRecord, 'parentId' | 'type' | 'title' | 'createdBy' | 'content'>;

function workspaceRootKind(content: Record<string, unknown> | undefined): 'family' | 'personal' | null {
  const kind = content?.workspaceRoot;
  if (kind === 'family' || kind === 'personal') {
    return kind;
  }
  return null;
}

export function isLegacyPersonalRootTitle(title: string | null): boolean {
  return title?.startsWith('Personal — ') ?? false;
}

export function isPersonalRootTitle(title: string | null): boolean {
  return title === PERSONAL_ROOT_TITLE || isLegacyPersonalRootTitle(title);
}

export function isFamilyRootBlock(block: WorkspaceRootBlock): boolean {
  if (block.parentId !== null || block.type !== 'page') {
    return false;
  }
  if (block.title === FAMILY_ROOT_TITLE) {
    return true;
  }
  return workspaceRootKind(block.content) === 'family';
}

export function isPersonalRootBlock(block: WorkspaceRootBlock, memberId: string): boolean {
  if (block.parentId !== null || block.type !== 'page' || block.title === FAMILY_ROOT_TITLE) {
    return false;
  }
  if (workspaceRootKind(block.content) === 'personal') {
    return block.createdBy === memberId;
  }
  return block.createdBy === memberId;
}

export function findFamilyRoot(pageRoots: BlockRecord[]): BlockRecord | undefined {
  return pageRoots.find((block) => isFamilyRootBlock(block));
}

export function findPersonalRoot(
  pageRoots: BlockRecord[],
  memberId: string,
): BlockRecord | undefined {
  const family = findFamilyRoot(pageRoots);
  return pageRoots.find(
    (block) => block.id !== family?.id && isPersonalRootBlock(block, memberId),
  );
}

export function isWorkspaceRootBlock(block: WorkspaceRootBlock, memberId: string): boolean {
  return isFamilyRootBlock(block) || isPersonalRootBlock(block, memberId);
}

export function displayBlockTitle(block: BlockRecord, memberId?: string | null): string {
  if (memberId && isPersonalRootBlock(block, memberId)) {
    return PERSONAL_ROOT_TITLE;
  }
  if (isFamilyRootBlock(block)) {
    return FAMILY_ROOT_TITLE;
  }
  return block.title?.trim() || (block.type === 'database' ? 'Database' : 'Untitled');
}
